/* eslint-disable @typescript-eslint/no-explicit-any */
import { useApiStore } from "@/store";
import { Input, InputRef } from "antd";
import {
  ChangeEvent,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
interface PinHookOptions {
  length: number;
  inputClassName?: string;
  inputGap?: string;
  seconds?: number;
  apiKey?: string;
  isConceal?: boolean;
}
export const usePin = ({
  length,
  inputClassName,
  inputGap,
  seconds,
  apiKey,
  isConceal = false,
}: PinHookOptions) => {
  const { setPayload } = useApiStore((state) => state);
  const [otpValues, setOtpValues] = useState<any>(Array(length).fill(""));
  const inputRefs = useRef<(InputRef | (null & { input: HTMLInputElement }))[]>(
    Array(length).fill(null)
  );
  const [counter, setCounter] = useState(seconds);

  const otpDefferedValues = useDeferredValue(otpValues);
  const isPinInputedCompletely = otpDefferedValues?.join("")?.length === length;

  const clearPinState = useCallback(() => {
    setOtpValues(undefined);
    if (apiKey) setPayload(apiKey, undefined);
  }, [apiKey]);

  useEffect(() => {
    if (counter) {
      const timer: any =
        counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [counter]);

  const handleInputChange = (index: number, value: string) => {
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    if (apiKey) setPayload(apiKey, newOtpValues);
    if (value && index < length - 1) {
      // Move to the next input field
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      if (otpValues[index]) {
        // Clear the current input if it's not empty
        const newOtpValues = [...otpValues];
        newOtpValues[index] = "";
        setOtpValues(newOtpValues);
      } else if (index > 0) {
        // Move to the previous input field and clear it
        inputRefs.current[index - 1]?.focus();
        const newOtpValues = [...otpValues];
        newOtpValues[index - 1] = "";
        setOtpValues(newOtpValues);
      }
    } else if (e.key === "Delete" && index < length - 1) {
      // Clear the current input and move to the next input field on Delete
      const newOtpValues = [...otpValues];
      newOtpValues[index] = "";
      setOtpValues(newOtpValues);
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    // Only proceed if pasted data contains numbers
    if (!/^\d+$/.test(pastedData)) return;

    const pastedArray = pastedData.split("").slice(0, length);
    const newOtpValues = Array(length).fill("");

    pastedArray.forEach((value, index) => {
      if (index < length) {
        newOtpValues[index] = value;
      }
    });

    setOtpValues(newOtpValues);
    if (apiKey) setPayload(apiKey, newOtpValues);
    // Focus on the last filled input or the next empty input
    const lastFilledIndex = newOtpValues.reduce(
      (lastIndex, val, index) => (val !== "" ? index : lastIndex),
      -1
    );
    const focusIndex =
      lastFilledIndex < length - 1 ? lastFilledIndex + 1 : lastFilledIndex;
    inputRefs.current[focusIndex]?.focus();
  };
  const inputComponents = useMemo(
    () => (
      <div className={`flex justify-between ${inputGap ?? "gap-6"}`}>
        {otpValues.map((value: any, index: number) => (
          <Input
            key={index}
            maxLength={1}
            placeholder="0"
            value={value ? (isConceal ? "*" : value) : ""}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              handleKeyDown(index, e)
            }
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const pin: any = e.target.value;
              if (!isNaN(pin)) handleInputChange(index, e.target.value);
            }}
            onPaste={(e) => handlePaste(e)}
            ref={(el) =>
              (inputRefs.current[index] = el as InputRef & {
                input: HTMLInputElement;
              })
            }
            className={
              inputClassName ??
              `h-[40px] w-[40px] text-center font-bold max-md:!min-w-[40px]`
            }
          />
        ))}
      </div>
    ),
    [
      otpValues,
      inputGap,
      handleKeyDown,
      handleInputChange,
      handlePaste,
      inputClassName,
      inputRefs,
      isConceal,
    ]
  );

  useEffect(() => {
    // Focus on the first input field when the component mounts
    inputRefs.current[0]?.input?.focus();
  }, []);

  useEffect(() => {
    // Focus on the button when all OTP values are filled
    if (otpValues.join("").length === length) {
      const lastInputRef = inputRefs.current[length - 1]?.input;
      if (lastInputRef) {
        lastInputRef.blur(); // Remove focus from the last input
      }
      // Focus on the button after a short delay to avoid interference with the last input
      const timeoutId = setTimeout(() => {
        if (lastInputRef) {
          lastInputRef.blur(); // Ensure last input is not focused
        }
        document.getElementById("verifyButton")?.focus();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [otpValues, length]);

  return {
    pinValue: otpDefferedValues?.join(""),
    inputComponents,
    setOtpValues,
    isPinInputedCompletely,
    counter,
    setCounter,
    clearPinState,
  };
};
